import './index.scss';
import {Button, Link, Tooltip, Typography} from '@customink/pigment-react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {withErrorBoundary} from 'react-error-boundary';
import PdpPriceDetailsTest from 'components/PdpPricingDetails';
import useBreakpoint from 'hooks/useBreakpoint';
import useImagePreloader from 'hooks/useImagePreloader';
import {pricingOnPDPTest} from 'src/tests/pricing_on_pdp_test';
import * as urlSetup from 'src/utils/url';
import rollbar from 'src/vendor/rollbar';
import {previewUpload, pricingOnPDP} from 'utils/constants/signalmanConstants';
import {
  getColorImage,
  getIxParams,
  getYdhParams,
  mmsImageFingerprint
} from 'utils/images';
import labLink from 'utils/labLink';
import {
  trackOrderFewerThanSixEvent,
  trackRequestSampleHoverEvent
} from 'utils/metrics/productDetailsMetrics';
import CurrentSelectedColorContext from '../Context/colorContext';
import EmployeeBlanks from '../EmployeeBlanks';
import ProductColorsAndSizes from '../ProductColorsAndSizes';
import DeliveryOptions from './DeliveryOptions';
import ProductCarousel from './ProductCarousel';
import ProductDescription from './ProductDescription';
import ProductDetails from './ProductDetails';
import ProductHeader from './ProductHeader';

const ProductHero = ({
  active,
  blanksOrdering,
  bulkQty,
  checkoutAction,
  colors,
  decoration,
  decoration_method: decorationMethod,
  defaultQty,
  formAuthToken,
  hasSingles,
  id,
  iframed,
  legacyStyleId,
  mainImage,
  material,
  minQty,
  name,
  preferSingles,
  primaryCategoryId,
  productProps,
  renderSampleForm,
  reviewRef,
  sampleLinkClass,
  sampleLinkHref,
  sampleOrderType,
  samplePrice,
  sampleQuote,
  sampleQuoteCid,
  selectedColor,
  similarRef,
  sizingText,
  sizingUrl,
  specialty,
  ttarpNumber
}) => {
  const {currentSelectedColor, setCurrentSelectedColor} = useContext(
    CurrentSelectedColorContext
  );
  const [currentColor, setCurrentColor] = useState(selectedColor);
  const [singles, setSingles] = useState(preferSingles);
  const sizes = currentColor?.sizes.filter((size) => size.status === 'active');
  const sizesList = sizes?.map((size) => size.conciseName);
  const [size, setSize] = useState(sizesList?.length > 2 ? sizesList[0] : null);
  const [email, setEmail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const isPreviewUploadTestEnabled = previewUpload === 'test';
  const customDesignPreview = isPreviewUploadTestEnabled
    ? ProductCatalog.LocalStorage.read('custom_design_preview')
    : null;
  const [defaultDesignPreview, setDefaultDesignPreview] = useState(() =>
    isPreviewUploadTestEnabled
      ? ProductCatalog.LocalStorage.read('default_design_preview')
      : null
  );
  const breakpoint = useBreakpoint();
  const preloadImage = useImagePreloader();
  const pdpPriceTest = pricingOnPDP === 'test';
  const firstRender = useRef(true);

  useEffect(() => {
    pricingOnPDPTest();
  }, []);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }
  });

  useEffect(() => {
    if (isPreviewUploadTestEnabled)
      ProductCatalog.LocalStorage.write(
        'default_design_preview',
        defaultDesignPreview || null
      );
  }, [defaultDesignPreview]);

  const updateUrl = (color) => {
    if (color.id) {
      let url = window.location.href;
      url = urlSetup.setQueryParam('PK', color.id, url);
      window.history.replaceState({}, '', url);
    }
  };

  const updateDataLayer = (value) => {
    const swatchData =
      document.querySelector('.pc-Style-dataLayer')?.dataset || {};
    if (value) {
      swatchData.variant = value.name;
      swatchData.colorid = value.id;
      swatchData.colorurl = `${urlSetup.baseUrl()}?PK=${value.id}`;
      swatchData.colorimage = `https://mms-images.out.customink.com/mms/images/catalog/colors/${value.id}/views/alt/front_small_extended.png`;
    }
    return ProductCatalog.gtmEvent(
      'detailImpression',
      'detailImpression',
      swatchData
    );
  };

  const changeColor = (value) => {
    if (value) {
      setCurrentColor(value);
      setCurrentSelectedColor(value);
      updateUrl(value);
      updateDataLayer(value);
    }
  };

  const postMessage = (payload) => {
    if (__ENV__ === 'production') {
      if (document.referrer.includes('customink.com')) {
        window.parent.postMessage(payload, '*');
      }
    } else {
      window.parent.postMessage(payload, '*');
    }
  };

  document.addEventListener('DOMContentLoaded', postMessage('domReady'));

  const handleLabLinkIframe = (e) => {
    e.preventDefault();
    const catElement = document.querySelector('meta[property=categoryinfo]');
    let catId = '';
    let catName = '';
    let catPath = '';
    if (catElement) {
      catId = parseInt(catElement.getAttribute('id'), 10);
      catName = catElement.getAttribute('name');
      [catPath] = catElement.getAttribute('path').split('?');
    }
    const productInfo = {
      decoration,
      defaultCategoryId: primaryCategoryId,
      minQty,
      name: currentColor.name,
      productId: currentColor.legacyColorId,
      size_range: `${sizes[0].conciseName}-${sizes[sizes.length - 1].conciseName}`,
      sizes: sizesList.join(', ')
    };
    const payload = {
      categoryPath: catPath,
      cid: currentColor.loadLabCid,
      colorId: urlSetup.getQueryParam(
        'PK',
        labLink(
          currentColor.legacyColorId,
          singles,
          currentColor.loadLabCid,
          legacyStyleId
        )
      ),
      data: [currentColor.id, productInfo, id, name, catId, catName],
      event: 'catalog:product-selected',
      keywords: urlSetup.getQueryParam(
        'KW',
        labLink(
          currentColor.legacyColorId,
          singles,
          currentColor.loadLabCid,
          legacyStyleId
        )
      ),
      singles,
      styleId: id
    };
    postMessage(payload);
  };

  const isIframed = () => {
    if (!active) {
      return (
        <Button fullWidth disabled>
          Out of Stock
        </Button>
      );
    }
    if (iframed) {
      postMessage('domReady');
      return (
        <Button
          fullWidth
          href="#"
          data-href={labLink(
            currentColor.legacyColorId,
            singles,
            currentColor.loadLabCid,
            legacyStyleId
          )}
          onClick={handleLabLinkIframe}>
          Select Product
        </Button>
      );
    }
    return (
      <Button
        fullWidth
        href={`${labLink(currentColor.legacyColorId, singles, currentColor.loadLabCid, legacyStyleId)}`}>
        {customDesignPreview ? 'Select Product' : 'Start Designing'}
      </Button>
    );
  };

  const imageFingerprint = mmsImageFingerprint({
    style_card_image_url: mainImage[currentColor.id].src
  });

  const buildColorImage = (color, width = 412) => {
    const ixParams = getIxParams(
      {},
      primaryCategoryId,
      mainImage[color.id].src
    );
    const ydhParams = getYdhParams(mainImage[color.id].src);
    ixParams.ixw = width;
    return getColorImage(
      color.id,
      {
        decoration_method: decorationMethod,
        imageFingerprint,
        material,
        style_card_image_url: mainImage[color.id].src
      },
      imageFingerprint,
      {...ixParams, ...ydhParams},
      false,
      iframed,
      true,
      false,
      customDesignPreview,
      setDefaultDesignPreview,
      defaultDesignPreview
    );
  };

  const handlePreloadImage = (color) => {
    preloadImage(buildColorImage(color));
  };

  const handleSetSingles = () => {
    setSingles(!singles);
    trackOrderFewerThanSixEvent(!singles);
    let url = window.location.href;
    if (!singles) {
      url = urlSetup.setQueryParam('quote_qty', 'min', url);
    } else {
      url = urlSetup.deleteQueryParam('quote_qty', url);
    }
    window.history.replaceState({}, '', url);
  };

  const handleSizingLink = () => {
    const popopts =
      'width=640,height=700,scrollbars=yes,toolbar=yes,menubar=no,location=yes,resizable=yes';
    window.open(sizingUrl, 'sizinglineup', popopts);
  };

  const handleQuoteLink = () => {
    const popopts =
      'width=400,height=615,scrollbars=yes,toolbar=yes,menubar=no,location=yes,resizable=yes';
    const url = new URL(`${window.location.origin}/quotes`);
    url.searchParams.append('product_id', currentColor.legacyColorId);
    window.open(url, 'quickquote', popopts);
  };

  const handleSizeChange = (event) => {
    setSize(event.target.value);
  };

  const handlePostalCodeChange = (event) => {
    setPostalCode(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const renderPla = () => {
    if (renderSampleForm) {
      return (
        <>
          <a
            className={`pc-plaSampleLink ${sampleLinkClass}`}
            href={sampleLinkHref}>
            <span className="pc-Style-hero-pricing-price-line sample-details">
              <span className="sample-cta">{sampleQuote}</span>{' '}
              <span className="sample-price"> {samplePrice}</span>
            </span>
          </a>
          <div className="pc-Style-hero-samples">
            <div className="pc-Style-reqSample">
              <form
                className="sb-Form pc-Style-samplesForm"
                action={checkoutAction}
                method="post">
                <input
                  name="authenticity_token"
                  value={formAuthToken}
                  type="hidden"
                />
                <input
                  id="order_type"
                  name="order_type"
                  value={sampleOrderType || ''}
                  type="hidden"
                />
                <input
                  id="design_cid"
                  name="design_cid"
                  value={sampleQuoteCid || ''}
                  type="hidden"
                />
                <input
                  id="product_id"
                  name="product_id"
                  value={currentSelectedColor.id}
                  type="hidden"
                />
                <div className="sb-Form-group">
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e)}
                    placeholder="Email"
                    required
                  />
                </div>
                <div className="pc-Style-samplesForm-group">
                  <div className="sb-Form-group">
                    <input
                      type="text"
                      name="postal_code"
                      id="postal_code"
                      value={postalCode}
                      onChange={(e) => handlePostalCodeChange(e)}
                      placeholder="Zip Code"
                      required
                    />
                  </div>
                  <div className="sb-Form-group">
                    <select
                      value={size}
                      required
                      name="sizes"
                      onChange={(e) => handleSizeChange(e)}>
                      <option value="">Select a Size</option>
                      {sizes.map((s) => {
                        return (
                          <option key={`sz-opt-${s.name}`} value={s.name}>
                            {s.conciseName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="sb-Form-group sb-Form-group--buttons">
                  <Button
                    name="commit"
                    value="Proceed to Checkout"
                    disable_with="Adding to Cart..."
                    className="sb-Btn sb-Btn--block sb-Btn--secondary"
                    data-disable-with="Proceed to Checkout"
                    variant="secondary"
                    type="submit">
                    Proceed to Checkout
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </>
      );
    }
    return null;
  };

  const handleMiniQuantityError = () => {
    setSingles(true);
    window.dataLayer.push({
      event: 'interaction',
      interaction_action: 'click available for small orders',
      interaction_category: 'pdp quantity',
      interaction_label: name
    });
  };

  return (
    <div className="pc-Style-Hero">
      <ProductCarousel buildColorImage={buildColorImage} />
      <div className="pc-Style-Hero-overview">
        <ProductHeader reviewRef={reviewRef} />
        {pdpPriceTest && !renderSampleForm && (
          <PdpPriceDetailsTest
            productProps={productProps}
            currentColor={currentColor}
            name={name}
            hasSingles={hasSingles}
            bulkQty={bulkQty}
            defaultQty={defaultQty}
            specialty={specialty}
            minQty={minQty}
            handleMiniQuantityError={handleMiniQuantityError}
          />
        )}
        <ProductColorsAndSizes
          colors={colors}
          currentColor={currentColor}
          changeColor={changeColor}
          hasSingles={hasSingles}
          toggleSingles={handleSetSingles}
          singles={singles}
          sizingText={sizingText}
          preloadImage={handlePreloadImage}
          styleId={id}
          similarRef={similarRef}
          renderSampleForm={renderSampleForm}
          breakpoint={breakpoint}
        />
        <Typography
          variant="captionRegular"
          className="pc-Style-Hero-fitAndSizing"
          component="p">
          <span className="pc-Style-Hero-fitAndSizing-sizingText">
            {sizingText}
          </span>
          {sizingUrl && (
            <Link onClick={handleSizingLink}>Fit & Sizing Guide</Link>
          )}
        </Typography>
        <div className="pc-Style-hero-cta">{isIframed()}</div>
        {renderPla()}
        <div className="pc-Style-Hero-bottomActions">
          <Tooltip
            onOpen={trackRequestSampleHoverEvent}
            title={
              <>
                <Typography variant="bodyLong1">
                  We know how important it is to find the right product style,
                  fit, and color. Don&apos;t worry — we&apos;re here to help.
                </Typography>
                <Typography variant="bodyLong1">
                  Call us at&nbsp;
                  <Typography variant="bodyLong1">{ttarpNumber}</Typography>
                  &nbsp; and we will be happy to ship a sample to you, typically
                  for a small fee plus shipping costs.
                </Typography>
                <Typography variant="bodyLong1">
                  We&apos;re also available to answer any questions you may have
                  about our products and can make recommendations based on your
                  particular needs.
                </Typography>
              </>
            }
            PopperProps={{className: 'pc-Style-Hero-tooltip'}}
            placement="bottom"
            arrow>
            <span className="pc-Styler-Hero-bottomActions">
              <Link variant="captionRegular">Request a Sample</Link>
            </span>
          </Tooltip>
          <div className="divider" />
          <span onClick={handleQuoteLink}>
            <Link variant="captionRegular">Get a Quote</Link>
          </span>
        </div>
        {blanksOrdering && (
          <div className="pc-Style-Hero-employeeBlanks-mobile">
            <EmployeeBlanks
              currentColor={currentColor}
              availableSizes={sizes}
            />
          </div>
        )}
        <ProductDescription />
        <ProductDetails />
        <DeliveryOptions />
        {blanksOrdering && (
          <div className="pc-Style-Hero-employeeBlanks">
            <EmployeeBlanks
              currentColor={currentColor}
              availableSizes={sizes}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ProductHeroWithErrorBoundary = withErrorBoundary(ProductHero, {
  fallback: <div>Something went wrong!</div>,
  onError: (error, componentStack) => {
    rollbar.error(error, componentStack);
  }
});

export default ProductHeroWithErrorBoundary;
